import { useContext, useMemo } from 'react';
import { useParams } from 'react-router';
import { Button, ButtonToolbar } from 'reactstrap';
import { mapModes } from '../../constants/map';
import { MapContext } from '../../contexts/MapContext';
import DeletingToolbar from './DeletingToolbar';
import CreateSectionButton from './buttons/CreateSectionButton';
import { sectionTypes } from '../../constants/fields';
import FlipHeadlandButton from './buttons/FlipHeadlandButton';
import CreateHeadlandButton from './buttons/CreateHeadlandButton';
import ButtonWithShortcut from '../basics/ButtonWithShortcut';
import { SHORTCUTS } from '../../constants/shortcuts';
import { captureEvent } from '../../utils/analytics';
import { layerIsCustom } from '../../utils/layers';

const LayerFeatureToolbar = () => {
  const { farmId } = useParams();
  const {
    mapState: {
      mapMode,
      setMapMode,
      activeLayerFeatureId,
      activeLayerFeature,
      activeLayer,
      activeLayerId,
    },
    layers: { deleteLayerFeature },
  } = useContext(MapContext);

  const { featureToFieldFeature, width } = activeLayer;

  const featureName = useMemo(
    () =>
      activeLayerFeature?.properties?.name ||
      `Feature ${activeLayer.data.features.indexOf(activeLayerFeature) + 1}`,
    [activeLayerFeature, activeLayer],
  );

  const fieldFeature = useMemo(
    () =>
      featureToFieldFeature && featureToFieldFeature(activeLayerFeature, width),
    [activeLayerFeature, featureToFieldFeature, width],
  );

  if (mapMode === mapModes.DELETING_LAYER_FEATURE) {
    return (
      <DeletingToolbar
        name={featureName}
        onCancel={() => {
          setMapMode(mapModes.LAYER_FEATURE_ENGAGED);
        }}
        onConfirm={() => {
          deleteLayerFeature(activeLayerFeatureId, farmId);
          if (layerIsCustom(activeLayer)) {
            captureEvent('custom-layer-delete-feature');
          }
          setMapMode(mapModes.LAYER_ENGAGED);
        }}
      />
    );
  }

  return (
    <ButtonToolbar>
      {activeLayer?.drawTool && (
        <>
          {!activeLayer?.disableUserEdits && (
            <Button
              onClick={() => setMapMode(mapModes.EDIT_LAYER_FEATURE)}
              color={
                mapMode === mapModes.EDIT_LAYER_FEATURE
                  ? 'primary'
                  : 'secondary'
              }
              size="sm"
              outline={mapMode !== mapModes.EDIT_LAYER_FEATURE}
              className="mr-2"
            >
              Edit
            </Button>
          )}
          {activeLayerId === 'HEADLANDS' && (
            <FlipHeadlandButton className="mr-2" outline size="sm" />
          )}
        </>
      )}
      <Button
        onClick={() => setMapMode(mapModes.DELETING_LAYER_FEATURE)}
        size="sm"
        outline
        className="mr-2"
      >
        Delete
      </Button>
      {featureToFieldFeature && (
        <CreateSectionButton
          disabled={fieldFeature?.geometry.type !== 'Polygon'}
          className="mr-2"
          availableSectionTypes={[sectionTypes.GRID, sectionTypes.KEYLINE]}
          initialSectionData={{
            fieldFeature,
            ...(activeLayerFeature?.properties?.name
              ? { name: `${activeLayerFeature.properties.name} Field` }
              : {}),
          }}
          sourceFeatureType={activeLayerId}
        />
      )}
      {
        // Anytime you can turn a layer feature into a field feature,
        // you can also turn it into a headland (except for headlands themselves).
        activeLayerId !== 'HEADLANDS' && activeLayer.featureToFieldFeature && (
          <CreateHeadlandButton
            outline
            size="sm"
            className="mr-2"
            feature={activeLayerFeature}
            onCreate={() =>
              captureEvent('headland-created-from-feature', {
                featureType: activeLayerId,
              })
            }
          />
        )
      }
      <ButtonWithShortcut
        shortcut={SHORTCUTS.CLOSE.key}
        onClick={() => setMapMode(mapModes.LAYER_ENGAGED, activeLayerId)}
        color="primary"
        size="sm"
      >
        Done
      </ButtonWithShortcut>
    </ButtonToolbar>
  );
};

export default LayerFeatureToolbar;
